import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFunnelAnalytics } from '../api/funnelAnalytics';
import type {
  FunnelAnalytics,
  FunnelBreakdown,
  FunnelStep,
} from '../types/funnelAnalytics';
import styles from './CheckoutFunnel.module.css';

/**
 * Checkout Funnel page.
 *
 * Visualizes our first-party funnel: device page viewed → device CTA clicked →
 * bundle viewed → checkout started → purchased. Data comes from
 * `/api/admin/funnel/analytics/` and is collected from deployment onward
 * (it is not backfilled), so windows longer than the time since launch will
 * simply show fewer days of data.
 */

const WINDOWS: ReadonlyArray<{ label: string; days: number }> = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
];

const STEP_COLORS = ['#60a5fa', '#a855f7', '#f59e0b', '#14b8a6', '#22c55e'];

function fmtNumber(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

function fmtPercent(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'never';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

interface BreakdownTableProps {
  title: string;
  breakdown: FunnelBreakdown;
}

/**
 * Renders one breakdown dimension (tier or color) as a small table:
 * rows = dimension values, columns = checkout started / purchased counts.
 */
function BreakdownTable({ title, breakdown }: BreakdownTableProps) {
  const started = breakdown['checkout_started'] || {};
  const purchased = breakdown['purchased'] || {};

  const keys = useMemo(() => {
    const set = new Set<string>([
      ...Object.keys(started),
      ...Object.keys(purchased),
    ]);
    return Array.from(set).sort((a, b) => (started[b] || 0) - (started[a] || 0));
  }, [started, purchased]);

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {keys.length === 0 ? (
        <div className={styles.empty}>No data in this window.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thLeft}>Segment</th>
              <th className={styles.thRight}>Checkout started</th>
              <th className={styles.thRight}>Purchased</th>
              <th className={styles.thRight}>Rate</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const s = started[k] || 0;
              const p = purchased[k] || 0;
              const rate = s > 0 ? (p / s) * 100 : 0;
              return (
                <tr key={k}>
                  <td className={styles.tdLeft}>{k}</td>
                  <td className={styles.tdRight}>{fmtNumber(s)}</td>
                  <td className={styles.tdRight}>{fmtNumber(p)}</td>
                  <td className={styles.tdRight}>{fmtPercent(rate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface FunnelBarsProps {
  steps: FunnelStep[];
}

function FunnelBars({ steps }: FunnelBarsProps) {
  return (
    <div className={styles.funnel}>
      {steps.map((step, i) => {
        const width = Math.max(step.conversion_from_top, 2);
        const color = STEP_COLORS[i % STEP_COLORS.length];
        return (
          <div key={step.step} className={styles.funnelRow}>
            <div className={styles.funnelMeta}>
              <span className={styles.funnelLabel}>{step.label}</span>
              <span className={styles.funnelCount}>
                {fmtNumber(step.count)}
                <span className={styles.funnelPct}>
                  {fmtPercent(step.conversion_from_top)} of top
                </span>
              </span>
            </div>
            <div className={styles.barTrack}>
              <div
                className={styles.barFill}
                style={{ width: `${width}%`, background: color }}
              />
            </div>
            {i > 0 && step.dropoff_from_prev > 0 ? (
              <span className={styles.dropoff}>
                ↓ {fmtPercent(step.dropoff_from_prev)} drop-off from{' '}
                {steps[i - 1].label.toLowerCase()}
              </span>
            ) : (
              <span className={styles.dropoffNeutral}>
                {i === 0 ? 'Top of funnel' : 'No drop-off'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CheckoutFunnel() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<FunnelAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async (windowDays: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFunnelAnalytics(windowDays);
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load funnel analytics';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  // Anchor headline + empty-state on the first step that has data (the
  // effective top of funnel). Until the storefront ships the
  // `device_page_viewed` event, this falls back to the device-CTA step so the
  // page keeps showing real data instead of collapsing to an empty state.
  const topStep = data?.steps.find((s) => s.count > 0) ?? data?.steps[0] ?? null;
  const topCount = topStep?.count ?? 0;
  const purchasedCount =
    data?.steps.find((s) => s.step === 'purchased')?.count ?? 0;

  const biggestDrop = useMemo(() => {
    if (!data) return null;
    let worst: FunnelStep | null = null;
    for (const step of data.steps.slice(1)) {
      if (!worst || step.dropoff_from_prev > worst.dropoff_from_prev) {
        worst = step;
      }
    }
    return worst;
  }, [data]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Checkout Funnel</h1>
          <p className={styles.subtitle}>
            How visitors move from the device page view through the CTA, bundle
            selection, checkout, and purchase. First-party data collected from
            launch onward — it is not backfilled.
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            Updated {formatRelativeTime(lastUpdated)}
          </span>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void load(days)}
            disabled={loading}
          >
            <span
              className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}
            >
              ↻
            </span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.windowBar}>
        <span className={styles.windowBarLabel}>Window</span>
        <div className={styles.windowPills}>
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              onClick={() => setDays(w.days)}
              className={`${styles.pill} ${days === w.days ? styles.pillActive : ''}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {data?.clamped_to_tracking_start && (
        <div className={styles.clampBanner}>
          <span className={styles.clampIcon}>ⓘ</span>
          <span>
            Page-view tracking began{' '}
            <strong>{formatDate(data.page_view_tracking_since)}</strong>. To keep
            conversions accurate, the funnel is shown from then (
            {(() => {
              const d = daysSince(data.window_start);
              return d != null ? `~${d}d` : 'fewer days';
            })()}{' '}
            of data) rather than the full {data.requested_days ?? days} days you
            selected. This limit clears itself as more page-view history builds
            up.
          </span>
        </div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(topCount)}</div>
          <div className={styles.statLabel}>Entered funnel ({days}d)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(purchasedCount)}</div>
          <div className={styles.statLabel}>Purchases</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtPercent(data?.overall_conversion)}
          </div>
          <div className={styles.statLabel}>Overall conversion</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {biggestDrop ? fmtPercent(biggestDrop.dropoff_from_prev) : '—'}
          </div>
          <div className={styles.statLabel}>
            {biggestDrop ? `Biggest drop · ${biggestDrop.label}` : 'Biggest drop'}
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loading}>Loading funnel…</div>
      ) : data ? (
        topCount === 0 ? (
          <div className={styles.empty}>
            No funnel events recorded in this window yet. Once visitors start
            moving through checkout, their progress appears here.
          </div>
        ) : (
          <>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Funnel</h3>
              <FunnelBars steps={data.steps} />
            </div>

            <div className={styles.breakdownGrid}>
              <BreakdownTable
                title="By bundle tier"
                breakdown={data.breakdowns.bundle_tier}
              />
              <BreakdownTable
                title="By device color"
                breakdown={data.breakdowns.device_color}
              />
            </div>
          </>
        )
      ) : null}

      <div className={styles.notes}>
        <h4 className={styles.notesTitle}>How to read this</h4>
        <ul>
          <li>
            Each step counts <strong>distinct sessions</strong> that reached it.
            A session is one shopper's journey (per browser tab).
          </li>
          <li>
            <strong>Device page viewed</strong> is the top of the funnel: a
            distinct session that loaded the device page (not raw pageviews, so
            a refresh doesn't double-count). It only starts accruing once the
            storefront ships the page-view event, so early windows may show no
            page-view data while the lower steps already have counts.
          </li>
          <li>
            <strong>Checkout started</strong> is recorded server-side when the
            Shopify cart is created, and <strong>Purchased</strong> is confirmed
            by the Shopify order webhook — both are reliable across the redirect
            to Shopify.
          </li>
          <li>
            Drop-off is the share of sessions lost between consecutive steps;
            the biggest drop highlights where to focus.
          </li>
        </ul>
      </div>
    </div>
  );
}
